import React from 'react';
import APPCONFIG from 'constants/Config';
import SelectField from '../../components/EnhancedSelectField';
import MenuItem from "@mui/material/MenuItem";
import QueueAnim from 'rc-queue-anim';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ReeValidate from 'ree-validate';
import * as authService from '../../services/authService';
import {
  bindingHash,
  sendLoginLink,
  savePending,
  LOGIN_LINK_PLATFORM_PORTAL,
} from '../../services/loginLinkService';
import CircularProgress from '@mui/material/CircularProgress';
import * as authActions from 'stores/actions/auth';
import * as types from 'constants/Types';
import ReCaptchaComponent from 'components/Recaptcha';
import EnhancedSnackBar from 'components/EnhancedSnackBar';
import {LB, JO } from 'country-flag-icons/react/3x2';
import { isEmpty } from 'utils/helpers';
import { connect } from 'react-redux';
import {
  isTracker,
  isAdmin,
  isOperator,
  isAccountant,
  isCustomer,
  isExternalViewer,
  isSuperAdmin,
} from 'utils/helpers';
import { push } from 'connected-react-router';
import {
  CASHBOX_APPROVAL_TOKEN_KEY,
  CASHBOX_APPROVAL_EMAILS_KEY,
  CONFIRM_EMAIL_TYPE_LOGIN_LINK,
} from 'constants/Paths';
import CashboxApprovalEmails from './CashboxApprovalEmails';

const MODE_PASSWORD = 'password';
const MODE_LINK = 'link';
const CARD_HEIGHT_DURATION = 280;

const readCashboxApprovalEmails = () => {
  try {
    if (!sessionStorage.getItem(CASHBOX_APPROVAL_TOKEN_KEY)) {
      return [];
    }
    const emails = JSON.parse(
      sessionStorage.getItem(CASHBOX_APPROVAL_EMAILS_KEY) || '[]',
    );
    return Array.isArray(emails)
      ? emails.filter((email) => typeof email === 'string' && email)
      : [];
  } catch (e) {
    return [];
  }
};

class Login extends React.Component {

  constructor(props, context) {
    super(props, context);

    this.cardRef = React.createRef();

    this.validator = new ReeValidate({
      email: 'required|email',
      password: 'required',
    });

    const cashboxApprovalEmails = readCashboxApprovalEmails();

    this.state = {
      brand: APPCONFIG.brand,
      credentials: {
        email: cashboxApprovalEmails[0] || '',
        password: '',
      },
      responseError: {
        isError: false,
        code: '',
        text: '',
      },
      isLoading: false,
      errors: this.validator.errors,
      country: 'LB',
      cashboxApprovalEmails,
      mode: MODE_PASSWORD,
      showPassword: false,
    };
  }

  handleSelectEmail = (email) => {
    this.handleChange({ target: { name: 'email', value: email } });
  };

  handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    this.setState(({ credentials }) => ({
      credentials: { ...credentials, [name]: value },
    }));
    this.validator.validate(name, value).then(() => {
      this.setState({ errors: this.validator.errors });
    });
  };

  handleToggleMode = () => {
    const card = this.cardRef.current;
    const fromHeight = card ? card.offsetHeight : null;
    this.validator.errors.remove('password');
    this.setState(
      ({ mode }) => ({
        mode: mode === MODE_LINK ? MODE_PASSWORD : MODE_LINK,
        errors: this.validator.errors,
        responseError: { isError: false, code: '', text: '' },
      }),
      () => this.animateCardHeight(fromHeight),
    );
  };

  animateCardHeight = (fromHeight) => {
    const card = this.cardRef.current;
    if (!card || fromHeight === null) {
      return;
    }
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const toHeight = card.offsetHeight;
    if (reduceMotion || fromHeight === toHeight) {
      return;
    }
    clearTimeout(this.cardHeightTimer);
    card.style.transition = 'none';
    card.style.overflow = 'hidden';
    card.style.height = `${fromHeight}px`;
    card.getBoundingClientRect();
    card.style.transition = `height ${CARD_HEIGHT_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
    card.style.height = `${toHeight}px`;
    this.cardHeightTimer = setTimeout(() => {
      card.style.transition = '';
      card.style.overflow = '';
      card.style.height = '';
    }, CARD_HEIGHT_DURATION + 20);
  };

  componentWillUnmount() {
    clearTimeout(this.cardHeightTimer);
  }

  handleTogglePassword = () => {
    this.setState(({ showPassword }) => ({ showPassword: !showPassword }));
  };

  handleFormSubmit = (event) => {
    event.preventDefault();
    if (this.state.isLoading) {
      return;
    }
    if (this.state.mode === MODE_LINK) {
      this.handleSendLink();
    } else {
      this.handleSubmit();
    }
  };

  handleSendLink = () => {
    const { email } = this.state.credentials;
    this.validator.validate('email', email).then((valid) => {
      if (!valid) {
        this.setState({ errors: this.validator.errors });
        return;
      }
      if (process.env.DISABLE_RECAPTCHA !== 'true' && !this.props.recaptcha) {
        this.props.dispatch(
          authActions.serverError('Recaptcha Requires Verification.'),
        );
        return;
      }
      this.setState({ isLoading: true });
      const linkEmail = email.trim().toLowerCase();
      const cashboxApprovalToken = sessionStorage.getItem(
        CASHBOX_APPROVAL_TOKEN_KEY,
      );
      bindingHash()
        .then((binding_hash) =>
          this.props.dispatch(
            sendLoginLink({
              email: linkEmail,
              platform: LOGIN_LINK_PLATFORM_PORTAL,
              binding_hash,
              ...(cashboxApprovalToken
                ? { cashbox_approval_token: cashboxApprovalToken }
                : {}),
            }),
          ),
        )
        .then(() => {
          savePending({ email: linkEmail, sent_at: Date.now() });
          this.props.dispatch(
            push({
              pathname: '/confirm-email',
              search: `?email=${encodeURIComponent(linkEmail)}&type=${CONFIRM_EMAIL_TYPE_LOGIN_LINK}`,
            }),
          );
        })
        .catch(({ error }) => {
          this.setState({ isLoading: false });
          this.props.dispatch(authActions.serverError(error));
        });
    });
  };

  handleSubmit = () => {
    const { credentials } = this.state;
    this.validator.validateAll(credentials).then((success) => {
      if (success) {
        if (process.env.DISABLE_RECAPTCHA === "true") {
          this.setState({
            isLoading: true,
          });
          this.submit(credentials); // Submit without reCAPTCHA verification
        } else {
        if (this.props.recaptcha) {
          this.setState({
            isLoading: true,
          });
          this.submit(
            { ...credentials, recaptcha_token: this.props.recaptcha },
            this.props.token,
          );
        } else {
          this.props.dispatch(
            authActions.serverError('Recaptcha Requires Verification.'),
          );
        }
      }
      } else {
        const { errors } = this.validator;
        this.setState({ errors: errors, credentials });
      }
    });
  };

  submit = (credentials, token = null) => {
    let input = {};
    if (this.props.location_Search) {
      const searchParams = new URLSearchParams(this.props.location_Search);
      let shop = searchParams.get('shop');
      let code = searchParams.get('code');
      if (token || token != '') {
        input = {
          ...credentials,
          thirdparty_code: code,
          thirdparty_shop: shop,
          thirdparty_token: !isEmpty(this.props.shop)
            ? this.props.shop.token
            : '',
          device_token: token,
          device_type: types.DEVICE_TYPE_WEB,
        };
      } else {
        input = {
          ...credentials,
          thirdparty_code: code,
          thirdparty_shop: shop,
          thirdparty_token: !isEmpty(this.props.shop)
            ? this.props.shop.token
            : '',
        };
      }
    } else {
      if (token) {
        input = {
          ...credentials,
          device_token: token,
          device_type: types.DEVICE_TYPE_WEB,
        };
      } else {
        input = {
          ...credentials,
        };
      }
    }

    if (!!localStorage.getItem('jwt_token')) {
      this.checkAuth();
    } else {
      localStorage.setItem("country", this.state.country);
      this.props
        .dispatch(authService.login(input))
        .catch(({ error, statusCode }) => {
          const responseError = {
            isError: true,
            code: statusCode,
            text: statusCode === 500 ? 'Unable to login, Please try again' : error,
          };
          this.setState({ responseError });
          this.setState({
            isLoading: false,
          });
        });
    }
  };

  componentDidMount() {
    this.setState({ isLoading: false });
    this.checkAuth();
  }

  componentWillUpdate(nextProps, nextState, nextContext) {
    localStorage.setItem("country", this.state.country);
    this.checkAuth(nextProps);
  }

  checkAuth = (props) => {
    const { from } = this.props.location.state || {
      from: { pathname: '/login' },
    };
    let isAuthenticated = !!localStorage.getItem('jwt_token');

    if (
      process.env.REACT_APP_IS_SHOPIFY === 'true' &&
      !this.state.credentials.email
    ) {
      isAuthenticated = false;
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('auth');
    }
    if (isAuthenticated) {
      if (from.pathname === '/login') {
        if (isTracker(props.user)) {
          from.pathname = '/app/map';
        } else if (isSuperAdmin(props.user)) {
          from.pathname = '/app/organizations/management/list';
        } else if (
          isAdmin(props.user) ||
          isOperator(props.user) ||
          isAccountant(props.user)
        ) {
          from.pathname = '/app/orders/pending';
        } else if (isExternalViewer(props.user)) {
          from.pathname = '/app/orders/express/active';
        } else if (process.env.REACT_APP_IS_SHOPIFY === 'true') {
          from.pathname = props.user.customer?.third_party?.shopify[0]?.id? '/app/third_party/shopify/' +props.user.customer.third_party.shopify[0].id : '/app/orders/pending';
        } else {
          from.pathname = localStorage.getItem('lastPageVisited')
            ? localStorage.getItem('lastPageVisited')
            : '/app/orders';
        }
      }
      this.props.dispatch(push(from.pathname));
    }
  };

  handleRequestClose = () => {
    this.props.dispatch(authActions.serverSuccessReset());
  };

  componentDidMount() {
    if (!isEmpty(this.props.shop)) {
      this.setState({
        ...this.state,
        credentials: {
          password: this.state.credentials.password,
          email: this.props.shop.email ? this.props.shop.email : '',
        },
      });
    } else {
      if (this.props.location_Search) {
        // since fetchStoreData is called when any request parameter is added
        // i need to skip it when having request_uri query parameter
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('request_uri')) {
          this.fetchStoreData();
        }
      }
    }
  }

  fetchStoreData() {
    this.setState({
      isLoading: true,
    });
    const searchParams = new URLSearchParams(this.props.location_Search);
    let shop = searchParams.get('shop');
    let code = searchParams.get('code');

    this.props
      .dispatch(authService.getShopifyShop(code, shop))
      .then((data) => {
        const responseError = {
          isError: false,
          code: '',
          text: '',
        };
        this.setState({
          ...this.state,
          isLoading: false,
          credentials: {
            password: this.state.credentials.password,
            email: data.data.shop.email,
          },
        });
      })
      .catch(({ error, statusCode }) => {
        const responseError = {
          isError: true,
          code: statusCode,
          text: error,
        };
        this.setState({
          isLoading: false,
          responseError,
        });
      });
  }

  render() {
    const {
      errors,
      credentials,
      cashboxApprovalEmails,
      mode,
      showPassword,
      isLoading,
    } = this.state;
    const isCashboxApproval = cashboxApprovalEmails.length > 0;
    const isLinkMode = mode === MODE_LINK;
    const isShopify = process.env.REACT_APP_IS_SHOPIFY === 'true';
    const emailError = errors.has('email') ? errors.first('email') : null;
    const passwordError = errors.has('password')
      ? errors.first('password')
      : null;

    return (
      <div className="page-login login-screen">
        <div className="main-body">
          <QueueAnim type="bottom" className="ui-animate">
            <div key="1">
              <div className="body-inner">
                <div className="card bg-white" ref={this.cardRef}>
                  <div className="card-content">
                    <section className="logo text-center">
                      <h1>
                        <a href="https://wakilni.com/">
                          <img
                            src="/assets/images/wakilni-logo-v2.png"
                            height="28"
                            alt={this.state.brand}
                          />
                        </a>
                      </h1>
                      <h3>Log In</h3>
                      {this.state.responseError.isError && (
                        <div className="login-response-error" role="alert">
                          {this.state.responseError.text}
                        </div>
                      )}
                    </section>
                    <form
                      className="login-form"
                      onSubmit={this.handleFormSubmit}
                      noValidate
                    >
                      {isShopify ? (
                        <div className="form-group login-field">
                          <SelectField
                            floatingLabelText="Country *"
                            value={this.state.country}
                            onChange={(event) =>
                              this.setState({
                                country: event.target.value,
                              })
                            }
                            fullWidth={true}
                          >
                            <MenuItem value={'LB'} leftIcon={<LB title="United States"/>} >Lebanon</MenuItem>
                            <MenuItem value={'JO'} leftIcon={<JO title="United States"/>}>Jordan</MenuItem>
                          </SelectField>
                        </div>
                      ) : null}
                      {isCashboxApproval ? (
                        <CashboxApprovalEmails
                          emails={cashboxApprovalEmails}
                          selectedEmail={credentials.email}
                          onSelect={this.handleSelectEmail}
                          errorText={emailError}
                        />
                      ) : (
                        <div className="login-field">
                          <label className="login-label" htmlFor="login-email">
                            Email
                          </label>
                          <div
                            className={`login-input${emailError ? ' has-error' : ''}`}
                          >
                            <span className="material-icons login-input-icon">
                              mail_outline
                            </span>
                            <input
                              id="login-email"
                              name="email"
                              type="email"
                              autoComplete="username"
                              autoCapitalize="none"
                              placeholder="Email"
                              value={credentials.email}
                              onChange={this.handleChange}
                            />
                          </div>
                          {emailError && (
                            <span className="login-field-error" role="alert">
                              {emailError}
                            </span>
                          )}
                        </div>
                      )}
                      {isLinkMode ? (
                        <div className="login-mode-panel" key={MODE_LINK}>
                          <div className="login-link-hint">
                            We'll email you a one-time link. Tap on the link in
                            the email to login directly.
                          </div>
                        </div>
                      ) : (
                        <div
                          className="login-mode-panel login-field login-field-password"
                          key={MODE_PASSWORD}
                        >
                          <label className="login-label" htmlFor="login-password">
                            Password
                          </label>
                          <div
                            className={`login-input${passwordError ? ' has-error' : ''}`}
                          >
                            <span className="material-icons login-input-icon">
                              lock_outline
                            </span>
                            <input
                              id="login-password"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="current-password"
                              placeholder="Password"
                              value={credentials.password}
                              onChange={this.handleChange}
                            />
                            <button
                              type="button"
                              className="login-input-action"
                              aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                              }
                              onClick={this.handleTogglePassword}
                            >
                              <span className="material-icons">
                                {showPassword ? 'visibility_off' : 'visibility'}
                              </span>
                            </button>
                          </div>
                          <div className="login-field-meta">
                            {passwordError && (
                              <span className="login-field-error" role="alert">
                                {passwordError}
                              </span>
                            )}
                            <Link
                              className="login-forgot"
                              to={{
                                pathname: '/forgot-password',
                                state: { email: credentials.email },
                              }}
                            >
                              Forgot Password?
                            </Link>
                          </div>
                        </div>
                      )}
                      <div className="login-recaptcha">
                        <ReCaptchaComponent />
                      </div>
                      <button
                        type="submit"
                        className="login-btn login-btn-primary"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <span className="login-btn-label" key={mode}>
                            {isLinkMode ? 'Send Link' : 'Log In'}
                          </span>
                        )}
                      </button>
                      {!isShopify && (
                        <React.Fragment>
                          <div className="login-divider">
                            <span>or</span>
                          </div>
                          <button
                            type="button"
                            className="login-btn login-btn-outline"
                            disabled={isLoading}
                            onClick={this.handleToggleMode}
                          >
                            <span className="login-btn-label" key={mode}>
                              <span className="material-icons">
                                {isLinkMode ? 'lock_outline' : 'link'}
                              </span>
                              {isLinkMode ? 'Use password instead' : 'Login with link'}
                            </span>
                          </button>
                        </React.Fragment>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </QueueAnim>
        </div>
        <EnhancedSnackBar />
      </div>
    );
  }
}

Login.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  successMsg: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  recaptcha: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    location_Search: state.router.location.search
  };
};

export default connect(mapStateToProps)(Login);
